import { useTranslation } from 'react-i18next';
import { Chip } from '@/components/ui';
import type { MatrixCell } from './types';
import { appTileLabel } from './coverageHelpers';
import {
    COVERAGE_BAR_TONE_CLASS,
    coverageBarTone,
    type MatrixAppColumn,
    type MatrixRoleRow,
} from './coverageMatrixHelpers';
import shared from './coverageShared.module.css';
import styles from './RoleMatrix.module.css';

/**
 * EAA-1817 (frame 55a): the three node builders the **Ma trận** view hands to
 * the kit `MatrixTable` — a column header, a row header, and a cell.
 *
 * They live here rather than inline in the view for the reason the file split
 * guidance gives: the view is then just "toolbar, table, footer", and the part
 * that has to match the handoff pixel-for-pixel is one file you can diff
 * against the frame.
 */

/** Column header: app code tile + app name, with `n/m` beneath — or the
 *  "chưa sẵn sàng" label for an app that cannot be linked into at all. */
export function RoleMatrixColumnHeader({ column }: { column: MatrixAppColumn }) {
    const { t } = useTranslation();
    return (
        <span className={styles.col}>
            <span className={shared.appTile} aria-hidden="true">
                {appTileLabel(column.code)}
            </span>
            <span className={styles.colText}>
                <span className={styles.colName}>{column.name}</span>
                <span className={styles.colMeta}>
                    {column.ready
                        ? `${column.coveredCount}/${column.totalRoleKeys}`
                        : t('console.roleCoverage.matrixView.appNotReady')}
                </span>
            </span>
        </span>
    );
}

/**
 * Row header: the standard role's Vietnamese NAME as the primary line and its
 * key beneath in mono, plus the row's coverage bar.
 *
 * The name-over-key order is the design's own 2026-08-24 correction ("người
 * dùng đọc theo tên, mã chỉ để đối chiếu") and it is the opposite of what this
 * screen shipped first, where the key was the primary line. Do not swap it
 * back: an admin scanning for "Thủ thư" cannot find it in a column of
 * `librarian`/`counselor`/`it_admin`.
 */
export function RoleMatrixRowHeader({ row }: { row: MatrixRoleRow }) {
    const tone = coverageBarTone(row.coveredCount, row.readyAppCount);
    const percent =
        row.readyAppCount > 0 ? Math.round((row.coveredCount / row.readyAppCount) * 100) : 0;
    return (
        <span className={styles.rowhead}>
            <span className={styles.rowheadText}>
                <span className={styles.rowheadName}>{row.roleName}</span>
                <code className={styles.rowheadKey}>{row.roleKey}</code>
            </span>
            <span className={styles.rowheadCoverage}>
                <span className={styles.rowheadRatio}>
                    {row.coveredCount}/{row.readyAppCount}
                </span>
                <span className={`${shared.progressTrack} ${styles.rowheadTrack}`}>
                    <span
                        className={`${shared.progressFill} ${COVERAGE_BAR_TONE_CLASS[tone]}`}
                        style={{ width: `${percent}%` }}
                    />
                </span>
            </span>
        </span>
    );
}

/**
 * Frame 55a's cell chip: 24px tall, `0 8px`, 11.5px. `Chip` sets its own
 * height/padding/size INLINE (the 26px table-row box), and a stylesheet rule
 * cannot beat an inline style without `!important` — so this goes through the
 * component's `style` prop, which the kit merges AFTER its own values precisely
 * so a caller can retune one instance. See the kit's styling contract in the
 * root CLAUDE.md ("Three ways to style a kit component").
 */
const CELL_CHIP_STYLE = { height: 24, padding: '0 8px', fontSize: 11.5 } as const;

export interface RoleMatrixCellProps {
    cell: MatrixCell;
    column: MatrixAppColumn;
    row: MatrixRoleRow;
    /** Omit to render the cell read-only (no manage capability). */
    onOpen?: (cell: MatrixCell) => void;
}

/**
 * One cell, in the four states frame 55a draws:
 *
 * | state    | drawn as                                          |
 * |----------|---------------------------------------------------|
 * | `linked` | green chip carrying the app-role NAME              |
 * | `multi`  | the first name's chip + a muted `+N`               |
 * | `paused` | red struck-through chip (the role the app lost)    |
 * | `none`   | dashed `+` box — click to link it here             |
 *
 * A column whose app is not ready renders a muted `—` and nothing clickable:
 * there is no catalog to pick a role from yet, so an affordance here would open
 * a picker with no options. That is a real difference from `none`, which is a
 * gap you CAN close, and the frame draws the two differently for that reason.
 *
 * The chip is the kit `Chip`: its `mapped`/`stale`/`add` tones are the same
 * three the frame draws, down to the inset ring and the dashed border, so this
 * cell contributes no palette of its own. `icon={false}` on every tone because
 * the grid's cells are 24px and a glyph beside the label pushes it into an
 * ellipsis — the legend above the table carries the meaning instead.
 *
 * EAA-1817 (2026-08-24): the chip prints the app role's NAME, not its key, with
 * the key in the tooltip — and `mono={false}` with it, since a Vietnamese phrase
 * set in monospace reads as a code that happens to have spaces in it. The names
 * come from the server (`CoverageCell.role_names`) rather than from a per-app
 * catalog lookup: that endpoint is per-application and gated on `APPS_VIEW`
 * (`@require_permissions_for_method`, not a superadmin check) — a different
 * permission from the one this board runs on — so labelling an eight-column grid
 * would have cost eight extra requests under a capability the viewer may not hold.
 */
export function RoleMatrixCell({ cell, column, row, onOpen }: RoleMatrixCellProps) {
    const { t } = useTranslation();

    if (!column.ready) {
        return (
            <span className={`${styles.cell} ${styles.cellUnavailable}`} aria-hidden="true">
                —
            </span>
        );
    }

    const stateText = t(`console.roleCoverage.cellState.${cell.state}`);
    const ariaLabel = t('console.roleCoverage.matrixView.cellAriaLabel', {
        roleName: row.roleName,
        appName: column.name,
        stateText,
    });
    const handleOpen = onOpen ? () => onOpen(cell) : undefined;

    if (cell.state === 'none') {
        return (
            <span className={styles.cell}>
                <Chip
                    tone="add"
                    onClick={handleOpen}
                    className={styles.cellAdd}
                    style={CELL_CHIP_STYLE}
                >
                    <span className="portal-sr-only">{ariaLabel}</span>
                </Chip>
            </span>
        );
    }

    const extraCount = cell.roles.length - 1;
    // EAA-1817: `role_names` is index-for-index with `roles` (server contract),
    // so the first name labels the chip and the first key is its tooltip. Falls
    // back to the key if a server ever sends the lists out of step.
    const firstName = cell.roleNames[0] ?? cell.roles[0] ?? stateText;
    const firstKey = cell.roles[0];

    return (
        <span className={styles.cell}>
            <Chip
                tone={cell.state === 'paused' ? 'stale' : 'mapped'}
                icon={false}
                mono={false}
                title={
                    firstKey
                        ? t('console.roleMap.table.appRoleCodeTooltip', { code: firstKey })
                        : undefined
                }
                onClick={handleOpen}
                className="rc-matrix-cell__chip"
                style={CELL_CHIP_STYLE}
            >
                {firstName}
            </Chip>
            {extraCount > 0 && (
                <span className={styles.cellMore} aria-hidden="true">
                    +{extraCount}
                </span>
            )}
            <span className="portal-sr-only">
                {extraCount > 0
                    ? t('console.roleCoverage.matrixView.cellSrExtra', {
                          label: ariaLabel,
                          count: extraCount,
                      })
                    : ariaLabel}
            </span>
        </span>
    );
}
